import type {
  Bonus,
  Differentiator,
  Faq,
  InsideImage,
  Module,
  PainPoint,
  PopupSale,
  Testimonial,
} from '../types/content'

export const PRODUCT_NAME = 'SAFETY INSPECTION PRO™'
export const CHECKOUT_URL = 'https://safety-inspection-pro.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '2160747241322362'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/cv6g2s5gyELSB2HHNyKoWo/Diseno_sin_titulo-15.webp'

export const inspectionFlow = ['PREPARE', 'INSPECT', 'IDENTIFY', 'PRIORITIZE', 'DOCUMENT', 'CORRECT']

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/rPGsbH5nThrfgz6ki44Dwn/Captura_de_pantalla_2026-10-06_a_la(s)_6.04.38_p._m..png',
    alt: 'What Type of Hazard Are You Looking At? — ten hazard categories from slips, trips and falls to work practices and temporary conditions',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/4gK43ZNPbnJhMstLojc3mA/Captura_de_pantalla_2026-10-06_a_la(s)_6.04.51_p._m..png',
    alt: 'Hazards Revealed — warehouse scene with eight numbered findings such as unstable stored load, liquid on floor and obstructed emergency equipment',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/jVnxg3YzJwTNnesVcqvybk/Captura_de_pantalla_2026-10-06_a_la(s)_6.05.11_p._m..png',
    alt: 'Would Emergency Equipment Be Ready When Needed? — emergency equipment checklist and exit route inspection questions',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/sAJNNEvShvb7H5XqDatcSs/Captura_de_pantalla_2026-10-06_a_la(s)_6.05.27_p._m..png',
    alt: 'When People and Vehicles Share Space — overhead map of forklift and pedestrian routes with intersection points and blind corners',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/pwTRspLEbQJZbrnLj2XPp4/Captura_de_pantalla_2026-10-06_a_la(s)_6.05.40_p._m..png',
    alt: 'PPE Inspection Is More Than Checking for a Hard Hat — hazard, required control, PPE, condition and use inspection sequence',
  },
]

export const painPoints: PainPoint[] = [
  {
    title: '😰 TOO MUCH TO CHECK. TOO EASY TO MISS SOMETHING.',
    body: 'Housekeeping? Electrical conditions? Machinery? PPE? Emergency equipment? Storage? Walkways? Fire risks? Work practices? Temporary hazards? Without a structured process, important observations can easily get lost in a general walkthrough.',
  },
  {
    title: '⚠️ YOU FOUND THE HAZARD—BUT WHAT HAPPENS NEXT?',
    body: 'Finding an unsafe condition is only the beginning. It still needs to be documented, evaluated, prioritized, assigned, corrected, followed up and properly closed. Without a clear system, findings can remain buried in reports while the original condition continues.',
  },
  {
    title: '📚 YOUR INSPECTION INFORMATION IS EVERYWHERE',
    body: "Checklists, photographs, notes, previous reports, corrective actions, spreadsheets, inspection records and follow-up information. The resources may exist—but they're scattered when you need a clear view of what was found and what still requires attention.",
  },
  {
    title: '😩 STOP RELYING ON MEMORY DURING SAFETY WALKTHROUGHS',
    body: "Walking through a workplace and simply looking for “anything unsafe” isn't a repeatable inspection process. Build every inspection around organized observations, hazard recognition, documented evidence, risk priorities and clear follow-up instead.",
  },
]

export const modules: Module[] = [
  {
    number: 1,
    title: 'SAFETY INSPECTION FOUNDATIONS & PLANNING™',
    body: 'Build a consistent inspection process before entering the work area with practical planning tools, inspection scope guides, frequency planners, responsibility frameworks, inspection routes and preparation resources for routine, scheduled and targeted workplace inspections.',
  },
  {
    number: 2,
    title: 'HAZARD IDENTIFICATION & RECOGNITION™',
    body: 'Recognize workplace hazards systematically with practical references covering slips and trips, falls, electrical hazards, machinery, chemicals, fire risks, ergonomics, material handling, housekeeping, storage, access, PPE, environmental conditions and unsafe work practices.',
  },
  {
    number: 3,
    title: 'WORKPLACE & EQUIPMENT INSPECTION CHECKLISTS™',
    body: 'Inspect work areas, warehouses, workshops, production floors, offices, storage areas, loading zones, emergency equipment, tools, machinery, electrical installations, PPE stations, walkways, exits and general facility conditions using structured ready-to-use inspection resources.',
  },
  {
    number: 4,
    title: 'RISK ASSESSMENT & PRIORITY CLASSIFICATION™',
    body: 'Turn inspection findings into clear priorities using practical risk matrices, severity and likelihood references, hazard-ranking frameworks and classification tools that help distinguish conditions requiring immediate attention from lower-priority improvements and scheduled corrective actions.',
  },
  {
    number: 5,
    title: 'INSPECTION REPORTING & DOCUMENTATION™',
    body: 'Transform observations into clear professional records with inspection report templates, hazard observation forms, finding logs, photo documentation layouts, corrective-action fields, inspection summaries and follow-up records designed to keep findings organized and actionable.',
  },
  {
    number: 6,
    title: 'CORRECTIVE ACTION & FOLLOW-UP SYSTEM™',
    body: 'Move findings from identification to documented resolution with corrective-action trackers, responsibility assignment tools, due-date systems, verification checklists, escalation frameworks, closure records and recurring-issue trackers that keep unresolved hazards visible.',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'SAFETY WALKTHROUGH FIELD KIT™',
    body: 'Run more focused workplace walkthroughs with practical field resources for inspection preparation, first-look observations, work-area findings, temporary conditions, evidence capture, quick interviews, unresolved questions and field-to-report transfer.',
  },
  {
    number: 2,
    title: 'CORRECTIVE ACTION DECISION TOOLKIT™',
    body: 'Turn findings into specific and verifiable actions with tools for defining the condition, considering appropriate controls, separating temporary and permanent measures, assigning ownership, documenting constraints and verifying corrective-action effectiveness.',
  },
  {
    number: 3,
    title: 'SAFETY PHOTO & EVIDENCE DOCUMENTATION PACK™',
    body: 'Create clearer inspection evidence with practical resources for contextual photographs, finding identification, captions, locations, equipment information, before-and-after comparisons, supporting records and organized evidence management.',
  },
  {
    number: 4,
    title: 'REPEAT HAZARD & TREND ANALYSIS SYSTEM™',
    body: 'Identify patterns hidden across multiple inspections with tools for tracking recurring findings, location hotspots, equipment patterns, work-activity trends, overdue actions and corrective measures that may not be producing sustained improvement.',
  },
  {
    number: 5,
    title: 'SAFETY INSPECTION COMMUNICATION & HANDOVER KIT™',
    body: 'Communicate findings clearly with ready-to-use formats for immediate notifications, supervisor handovers, maintenance requests, shift communication, management summaries, action follow-ups, escalations and verified closure.',
  },
]

export const differentiators: Differentiator[] = [
  {
    icon: 'hardhat',
    title: 'Practical inspection resources',
    body: 'Find the planning tool, checklist, hazard reference, reporting template, risk assessment or follow-up resource you need.',
  },
  {
    icon: 'search',
    title: 'Structured inspection process',
    body: 'Move from inspection preparation to workplace observation, hazard identification, risk prioritization, documentation and corrective-action follow-up.',
  },
  {
    icon: 'alert',
    title: '6 dedicated inspection areas',
    body: 'Inspection planning, hazard recognition, workplace and equipment inspections, risk assessment, reporting and corrective-action management.',
  },
  {
    icon: 'gift',
    title: '5 practical professional bonuses',
    body: 'Field walkthroughs, corrective-action decisions, evidence documentation, recurring-hazard analysis and professional communication tools.',
  },
  {
    icon: 'refresh',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever you need to prepare an inspection, evaluate a workplace, document findings, organize actions or verify that an issue has been resolved.',
  },
]

export const offerBullets = [
  '6 comprehensive safety inspection modules',
  '5 strategic professional bonuses',
  'Workplace hazard identification resources',
  'Ready-to-use inspection checklists',
  'Risk assessment & priority classification',
  'Professional inspection reporting tools',
  'Corrective-action tracking & follow-up',
  'Evidence, trend analysis & communication resources',
]

export const testimonials: Testimonial[] = [
  {
    name: 'Karen Mitchell, 42',
    role: 'Workplace Safety Coordinator',
    location: 'Columbus, United States',
    headline: 'My inspections are much easier to structure.',
    quote:
      'Instead of walking through an area trying to remember everything I should be looking for, I now have a clear process from preparation through documentation and follow-up. It makes the entire inspection much easier to organize.',
  },
  {
    name: 'Daniel Hughes, 47',
    role: 'Safety Inspector',
    location: 'Leeds, United Kingdom',
    headline: 'The hazard recognition resources are extremely useful.',
    quote:
      'There are always several things happening at once during a workplace inspection. Having the different hazard categories and observation points organized together helps me inspect an area much more systematically.',
  },
  {
    name: 'Priya Sandhu, 35',
    role: 'EHS Professional',
    location: 'Toronto, Canada',
    headline: 'Much better than scattered checklists and notes.',
    quote:
      'I used to have inspection forms, photographs, corrective actions and previous findings stored in different places. Having the process organized as one system gives me a much cleaner way to manage inspections.',
  },
  {
    name: 'Mark Jennings, 51',
    role: 'Facility Safety Coordinator',
    location: 'Perth, Australia',
    headline: 'The corrective-action tools make a real difference.',
    quote:
      'Finding the problem was never the hardest part. Keeping track of what needed to happen afterward was. I like being able to connect the finding, responsible person, action, evidence and verification instead of losing track after the report is issued.',
  },
  {
    name: 'Laura Bennett, 39',
    role: 'Workplace Safety Professional',
    location: 'Denver, United States',
    headline: 'A practical system I can keep using.',
    quote:
      "This isn't something I would read once and forget. The inspection tools, hazard references, reporting resources and follow-up materials are things I can return to whenever I'm preparing or completing an inspection.",
  },
]

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Safety inspectors, EHS/HSE professionals, facility and maintenance professionals, workplace safety personnel, supervisors and other qualified professionals involved in workplace safety inspections and corrective-action follow-up.',
  },
  {
    q: 'What types of workplace hazards does it cover?',
    a: 'The system includes practical resources for identifying and documenting representative hazards involving housekeeping, access, slips and trips, falls, machinery, electrical conditions, storage, fire safety, PPE, material handling, ergonomics, work practices, emergency equipment and general workplace conditions.',
  },
  {
    q: 'Does it include ready-to-use inspection checklists?',
    a: "Yes. Workplace and equipment inspection resources are a central part of the system, covering different areas, conditions and inspection points while remaining adaptable to the user's specific workplace.",
  },
  {
    q: 'Does it help after a hazard has been identified?',
    a: 'Yes. The system includes resources for risk prioritization, professional reporting, corrective-action assignment, evidence documentation, follow-up, effectiveness verification and closure.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. SAFETY INSPECTION PRO™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a professional inspection and reference system designed to help organize workplace safety inspection activities.',
  },
  {
    q: 'Is it another theory-heavy safety ebook?',
    a: 'No. The system focuses on inspection workflows, hazard-recognition references, checklists, risk assessment tools, documentation templates, corrective-action resources and practical follow-up materials.',
  },
  {
    q: 'Does this guarantee regulatory compliance or replace professional safety requirements?',
    a: 'No. Safety requirements vary by workplace, industry, jurisdiction and activity. Applicable regulations, organizational procedures, specialized technical requirements and qualified professional judgment should always be followed.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: "What if it isn't right for me?",
    a: "You're covered by the 7-Day Money-Back Guarantee. If it isn't right for you, contact us within 7 days for a full refund according to the refund terms.",
  },
]

export const popupSales: PopupSale[] = [
  { name: 'Karen M.', city: 'Dallas', country: 'United States' },
  { name: 'Tom R.', city: 'Birmingham', country: 'United Kingdom' },
  { name: 'Kevin L.', city: 'Calgary', country: 'Canada' },
  { name: 'Sarah B.', city: 'Perth', country: 'Australia' },
  { name: 'Brian W.', city: 'Houston', country: 'United States' },
  { name: 'Gareth P.', city: 'Cardiff', country: 'United Kingdom' },
  { name: 'Melissa D.', city: 'Hamilton', country: 'Canada' },
  { name: 'Luke T.', city: 'Melbourne', country: 'Australia' },
  { name: 'Jason K.', city: 'Denver', country: 'United States' },
  { name: 'Emma S.', city: 'Glasgow', country: 'United Kingdom' },
  { name: 'Chris D.', city: 'Winnipeg', country: 'Canada' },
  { name: 'Rachel O.', city: 'Adelaide', country: 'Australia' },
]
